import {
  CountryCode,
  HumanNamePrefix,
  LanguagePreference,
  LanguagePreferenceDisplay,
  PersonIdentityCode,
} from '@quromedical/fhir-common';
import {
  AlternatePhoneNumberSchema,
  type PersonContact,
  type PersonContactWithAddressEmergencyAccess,
  type PersonGeneral,
  PrimaryPhoneNumberSchema,
} from '@quromedical/models';
import { compact } from '@quromedical/utils';
import type { Field, SelectOption } from 'components/types';
import type { Fetcher, RadioOption } from 'design-system';
import { strings } from 'strings';

export const titleFetcher = async (): Promise<
  SelectOption<undefined, HumanNamePrefix>[]
> => [
  {
    display: strings.TitleMr,
    value: HumanNamePrefix.mr,
  },
  {
    display: strings.TitleMrs,
    value: HumanNamePrefix.mrs,
  },
  {
    display: strings.TitleMiss,
    value: HumanNamePrefix.miss,
  },
  {
    display: strings.TitleDr,
    value: HumanNamePrefix.dr,
  },
  {
    display: strings.TitleSr,
    value: HumanNamePrefix.sr,
  },
  {
    display: strings.TitleProf,
    value: HumanNamePrefix.prof,
  },
];

export const languageFetcher = async (): Promise<SelectOption<undefined>[]> => [
  {
    display: LanguagePreferenceDisplay[LanguagePreference.Afrikaans],
    value: LanguagePreference.Afrikaans,
  },
  {
    display: LanguagePreferenceDisplay[LanguagePreference.English],
    value: LanguagePreference.English,
  },
  {
    display: LanguagePreferenceDisplay[LanguagePreference.Ndebele],
    value: LanguagePreference.Ndebele,
  },
  {
    display: LanguagePreferenceDisplay[LanguagePreference.Pedi],
    value: LanguagePreference.Pedi,
  },
  {
    display: LanguagePreferenceDisplay[LanguagePreference.Sotho],
    value: LanguagePreference.Sotho,
  },
  {
    display: LanguagePreferenceDisplay[LanguagePreference.Swati],
    value: LanguagePreference.Swati,
  },
  {
    display: LanguagePreferenceDisplay[LanguagePreference.Tsonga],
    value: LanguagePreference.Tsonga,
  },
  {
    display: LanguagePreferenceDisplay[LanguagePreference.Tswana],
    value: LanguagePreference.Tswana,
  },
  {
    display: LanguagePreferenceDisplay[LanguagePreference.Venda],
    value: LanguagePreference.Venda,
  },
  {
    display: LanguagePreferenceDisplay[LanguagePreference.Xhosa],
    value: LanguagePreference.Xhosa,
  },
  {
    display: LanguagePreferenceDisplay[LanguagePreference.Zulu],
    value: LanguagePreference.Zulu,
  },
];

type Gender = fhir4.Patient['gender'];

export type DefinedGender = Exclude<Gender, undefined>;

export type ContactWithOutCoordinates = Omit<
  PersonContactWithAddressEmergencyAccess,
  'addressCoordinates'
>;

export const genderDisplay: Record<DefinedGender, string> = {
  female: strings.GenderFemale,
  male: strings.GenderMale,
  other: strings.GenderOther,
  unknown: strings.GenderUnknown,
};

export const genderOptions: RadioOption<Gender>[] = [
  {
    display: strings.GenderMale,
    value: 'male',
  },
  {
    display: strings.GenderFemale,
    value: 'female',
  },
  {
    display: strings.GenderOther,
    value: 'other',
  },
  {
    display: strings.GenderUnknown,
    value: 'unknown',
  },
];

interface WithPrefix {
  prefixName?: HumanNamePrefix;
}

export const prefixRow: Field<WithPrefix> = {
  subfields: [
    {
      type: 'combobox-single',
      key: 'prefixName',
      label: strings.LabelTitle,
      fetcher: titleFetcher,
    },
  ],
  icon: 'person-outline',
};

interface WithName {
  givenName?: string;
  familyName?: string;
}

export const nameRow: Field<WithName> = {
  subfields: [
    {
      type: 'text-box',
      key: 'givenName',
      label: strings.LabelName,
    },
    {
      type: 'text-box',
      key: 'familyName',
      label: strings.LabelSurname,
    },
  ],
};

interface WithGender {
  gender?: Gender;
}

export const genderRow: Field<WithGender> = {
  subfields: [
    {
      type: 'radio-group',
      key: 'gender',
      label: strings.Gender,
      options: genderOptions,
    },
  ],
  icon: 'gender-male-female',
};

interface WithPhone {
  telecomPhoneNumbers?: string[];
}

export const createPhoneRow = (
  canShowPrimaryError: boolean,
  canShowSecondaryError: boolean,
): Field<WithPhone> => ({
  subfields: [
    {
      type: 'phone-number',
      // This is used so formkik can populate an array for us
      key: 'telecomPhoneNumbers.0' as keyof WithPhone,
      label: strings.Phone,
      placeholder: strings.FormPlaceholderPhonePrimary,
      errorMessage: canShowPrimaryError && strings.PhoneNumberLineError,
    },
    {
      type: 'phone-number',
      // This is used so formkik can populate an array for us
      key: 'telecomPhoneNumbers.1' as keyof WithPhone,
      label: strings.Phone2,
      placeholder: strings.FormPlaceholderPhoneAlternative,
      errorMessage: canShowSecondaryError && strings.PhoneNumberLineError,
    },
  ],
  icon: 'phone-iphone',
});

interface WithEmail {
  telecomEmail?: string;
}

interface WithLanguagePreference {
  languagePreference?: string;
}

export const emailRow: Field<WithEmail & WithLanguagePreference> = {
  subfields: [
    {
      type: 'text-box',
      key: 'telecomEmail',
      label: strings.Email,
    },
    {
      type: 'combobox-single',
      key: 'languagePreference',
      label: strings.LanguagePreference,
      searchable: false,
      fetcher: languageFetcher,
    },
  ],
  icon: 'email',
};

export interface WithAddress {
  addressLines?: string[];
  addressCity?: string;
  addressPostalCode?: string;
  addressCountry?: string;
  addressPlaceId?: string;
}

interface WithEmergencyAccess {
  addressEmergencyAccess?: string;
}

export const countryFetcher: SelectOption[] = [
  {
    display: strings.SouthAfrica,
    value: CountryCode.SouthAfrica,
  },
  {
    display: strings.Namibia,
    value: CountryCode.Namibia,
  },
];

/**
 * if the address fetcher is not provided the form will only allow for manual address input
 */
export const createAddressRows = (
  addressSearchFetcher?: Fetcher<string>,
): Field<WithAddress>[] =>
  compact([
    addressSearchFetcher && {
      subfields: [
        {
          type: 'combobox-single',
          key: 'addressPlaceId',
          label: 'Search Addresses',
          searchable: true,
          fetcher: addressSearchFetcher,
        },
      ],
      icon: 'pin-drop',
    },
    {
      subfields: [
        {
          type: 'text-box',
          key: 'addressLines.0' as keyof WithAddress,
          label: strings.LabelAddressLine1,
          errorMessage: strings.AddressLine1Error,
        },
      ],
    },
    {
      subfields: [
        {
          type: 'text-box',
          key: 'addressLines.1' as keyof WithAddress,
          label: strings.LabelAddressLine2,
          placeholder: strings.FormPlaceholderAddressLine2,
        },
      ],
    },
    {
      subfields: [
        {
          type: 'text-box',
          key: 'addressCity',
          label: strings.LabelCity,
        },
      ],
    },
    {
      subfields: [
        {
          type: 'text-box',
          key: 'addressPostalCode',
          label: strings.LabelPostalCode,
        },
      ],
    },
    {
      subfields: [
        {
          type: 'combobox-single',
          key: 'addressCountry',
          fetcher: countryFetcher,
          label: strings.LabelCountry,
        },
      ],
    },
  ]);

const emergencyAccessField: Field<WithEmergencyAccess> = {
  subfields: [
    {
      type: 'text-box',
      key: 'addressEmergencyAccess',
      label: strings.LabelInCaseOfEmergency,
      multiline: true,
    },
  ],
  icon: 'error-outline',
};

export const idTypeFetcher = async (): Promise<SelectOption[]> => [
  {
    display: strings.LabelSAIDNumber,
    value: PersonIdentityCode.saIdNumber,
  },
  {
    display: strings.LabelNamibiaIDNumber,
    value: PersonIdentityCode.namibiaIdNumber,
  },
  {
    display: strings.LabelPassportNumber,
    value: PersonIdentityCode.passportNumber,
  },
];

interface WithId {
  identifierCode?: PersonIdentityCode;
  identifierValue?: string;
}

export const idRow: Field<WithId> = {
  subfields: [
    {
      type: 'combobox-single',
      label: strings.LabelIdentifierType,
      key: 'identifierCode',
      fetcher: idTypeFetcher,
    },
    {
      type: 'text-box',
      key: 'identifierValue',
      label: strings.LabelIDNumber,
    },
  ],
};

interface WithDateOfBirth {
  birthDate?: string;
}

export const birthDate: Field<WithDateOfBirth> = {
  subfields: [
    {
      maskType: 'date',
      type: 'date-component',
      key: 'birthDate',
      label: strings.DateOfBirth,
      validRange: { endDate: new Date() },
    },
  ],
  icon: 'cake',
};

export const personGeneralFields: Field<Partial<PersonGeneral>>[] = [
  prefixRow,
  nameRow,
  idRow,
  genderRow,
  birthDate,
];

export const minimalPersonGeneralFields: Field<Partial<PersonGeneral>>[] = [
  prefixRow,
  nameRow,
  genderRow,
];

export const createPersonContactFields = (
  isPrimaryValid: boolean,
  isSecondaryValid: boolean,
  addressFetcher?: Fetcher<string>,
  hasEmergencyAccess = false,
): Field<ContactWithOutCoordinates>[] => {
  const phone = createPhoneRow(!isPrimaryValid, !isSecondaryValid);
  const addressEmergencyAccess = hasEmergencyAccess
    ? emergencyAccessField
    : undefined;
  const addressRows = createAddressRows(addressFetcher);

  return compact<Field<ContactWithOutCoordinates>>([
    phone,
    emailRow,
    addressEmergencyAccess,
    ...addressRows,
  ]);
};

/**
 * Get the resolved contact form fields based on the current form state
 */
export const resolveFormContactFields = (
  formState: Partial<PersonContact>,
  hasEmergencyAccess?: boolean,
  addressFetcher?: Fetcher<string>,
): Field<ContactWithOutCoordinates>[] => {
  const isPrimaryValid = PrimaryPhoneNumberSchema.safeParse(
    formState.telecomPhoneNumbers?.[0],
  ).success;

  // the secondary number may be empty or match the required format
  const isSecondaryEmpty = !formState.telecomPhoneNumbers?.[1];

  const isSecondaryValid =
    isSecondaryEmpty ||
    AlternatePhoneNumberSchema.safeParse(formState.telecomPhoneNumbers?.[1])
      .success;

  return createPersonContactFields(
    isPrimaryValid,
    isSecondaryValid,
    addressFetcher,
    hasEmergencyAccess,
  );
};
